import PropTypes from 'prop-types';
import React, { useLayoutEffect, useRef, useState } from 'react';
import styled, { css, useTheme } from 'styled-components';
import { Icon, inkA, orange, PlainButton, tokens } from 'components/v3';
import { brand } from '../../shared/brand';
import { fieldTypeLabel } from '../../shared/fieldTypes';
import BrandMark from '../fields/BrandMark';
import FieldPreview from '../fields/FieldPreview';
import { DRAG_HANDLE_CLASS } from '../state/sortableBridge';

// One field on the canvas (design: .cfield). The whole card is a select
// button laid over the static preview; the drag handle, the action buttons
// and any upload button sit above it as siblings, never inside it.
//
// variant 'desktop': grip strip on the left (with the brand mark when the
//   field pre-fills), Duplicate / Delete on hover. The Type / Required /
//   Pre-fills tags sit top-right on the label's row and slide 64px left
//   while Duplicate / Delete show; a card under 480px wide puts them back
//   under the label (design: .cfield-head, @container max-width 479px).
// variant 'mobile': an orange pencil (Edit) and ⋯ (More) on every card;
//   tapping the card also edits. Tags stay under the label.
// locked (w9-plan §6.5): the card is read-only with a lock glyph and the
//   tooltip "Part of the IRS W-9": no drag handle, no Duplicate / Delete,
//   no Edit / ⋯, and a click selects nothing. Default false.

// Room the desktop Duplicate / Delete cluster needs (two 28px buttons): the
// label keeps it clear and the tags slide this far left while it shows.
export const ACTION_ROOM = 64;
export const NARROW_CARD = 480;

const ERROR_RED = '#ED4337';

const Card = styled.div`
  position: relative;
  display: flex;
  align-items: stretch;
  border-radius: 10px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  box-shadow: ${(p) => p.$shadow};
  transition: background 150ms ease, border-color 150ms ease,
    box-shadow 150ms ease;

  &:hover {
    border-color: ${(p) => p.$hoverBorder};
  }

  .fb-card-actions {
    opacity: ${(p) => (p.$showActions ? 1 : 0)};
    transition: opacity 150ms ease;
  }

  &:hover .fb-card-actions,
  &:focus-within .fb-card-actions {
    opacity: 1;
  }

  .fb-field-tags {
    transition: transform 150ms ease;
  }

  ${(p) =>
    p.$slideTags &&
    css`
      &:hover .fb-field-tags,
      &:focus-within .fb-field-tags {
        transform: translateX(-${ACTION_ROOM}px);
      }
    `}

  ${(p) =>
    p.$slideTags &&
    p.$showActions &&
    css`
      .fb-field-tags {
        transform: translateX(-${ACTION_ROOM}px);
      }
    `}

  @media (prefers-reduced-motion: reduce) {
    .fb-field-tags {
      transition: none;
    }
  }
`;

const Handle = styled.div`
  position: relative;
  z-index: 2;
  flex: none;
  width: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 10px 0;
  cursor: grab;
  touch-action: none;
  color: ${(p) => p.$color};
  background: ${(p) => p.$bg};
  border-right: 1px solid ${(p) => p.$line};
  border-radius: 9px 0 0 9px;
  transition: background 150ms ease, color 150ms ease;

  &:hover {
    color: white;
    background: ${orange(1)};
  }

  &:active {
    cursor: grabbing;
  }
`;

const SelectButton = styled(PlainButton)`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  border-radius: 10px;
`;

const MobileButton = styled(PlainButton)`
  width: 42px;
  height: 42px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border: 1px solid ${(p) => p.$border};
  background: ${(p) => p.$bg};
  color: ${(p) => p.$color};

  &:active {
    transform: scale(0.94);
  }
`;

export const LOCKED_TIP = 'Part of the IRS W-9';

// A lock, on the 24×24 lucide grid like the v3 icons.
export const LockGlyph = ({ size, color }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ display: 'block', flexShrink: 0 }}
  >
    <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
    <path d="M7 11V7a5 5 0 0 1 10 0v4" />
  </svg>
);

LockGlyph.propTypes = { size: PropTypes.number, color: PropTypes.string };
LockGlyph.defaultProps = { size: 16, color: 'currentColor' };

// The locked card's badge (design: a muted lock in the actions' corner).
const LockBadge = ({ name }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      role="img"
      aria-label={`${name} is locked: ${LOCKED_TIP}`}
      title={LOCKED_TIP}
      data-tip={LOCKED_TIP}
      data-fb-locked=""
      style={{
        position: 'absolute',
        top: 10,
        right: 10,
        zIndex: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 26,
        height: 26,
        borderRadius: 7,
        background: inkA(theme, 0.06),
        border: '1px solid ' + t.border,
        color: t.faint,
      }}
    >
      <LockGlyph size={14} />
    </div>
  );
};

LockBadge.propTypes = { name: PropTypes.string.isRequired };

const SourceMark = ({ size }) => (
  <BrandMark size={size} title={`From the person’s ${brand().name} account`} />
);

SourceMark.propTypes = { size: PropTypes.number.isRequired };

// The design's compact cluster (.cfield-row-actions): 28px buttons, the
// glyph goes orange on hover; Delete is red until hovered.
const SmallAction = styled(PlainButton)`
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  color: ${(p) => p.$color};
  transition: background 120ms ease, color 120ms ease;

  &:hover,
  &:focus-visible {
    color: ${orange(1)};
    background: ${(p) => p.$hoverBg};
  }
`;

const DesktopActions = ({ name, onDuplicate, onDelete }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      className="fb-card-actions"
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 3,
        display: 'flex',
        gap: 2,
        padding: 2,
        borderRadius: 8,
        background: t.card,
        border: '1px solid ' + t.border,
      }}
    >
      <SmallAction
        title={`Duplicate ${name}`}
        aria-label={`Duplicate ${name}`}
        onClick={onDuplicate}
        $color={inkA(theme, 0.6)}
        $hoverBg={inkA(theme, 0.06)}
      >
        <Icon name="copy" size={15} color="currentColor" />
      </SmallAction>
      <SmallAction
        title={`Delete ${name}`}
        aria-label={`Delete ${name}`}
        onClick={onDelete}
        $color="#D63A24"
        $hoverBg="rgba(214, 58, 36, 0.12)"
      >
        <Icon name="trash-2" size={15} color="currentColor" />
      </SmallAction>
    </div>
  );
};

DesktopActions.propTypes = {
  name: PropTypes.string.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

const MobileActions = ({ name, onEdit, onMore }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <div
      style={{
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 3,
        display: 'flex',
        gap: 6,
      }}
    >
      <MobileButton
        aria-label={`Edit ${name}`}
        onClick={onEdit}
        $border={orange(0.35)}
        $bg={orange(0.1)}
        $color={t.primary}
      >
        <Icon name="pencil" size={18} color="currentColor" />
      </MobileButton>
      <MobileButton
        aria-label={`More actions for ${name}`}
        aria-haspopup="dialog"
        onClick={onMore}
        $border={t.border}
        $bg={inkA(theme, 0.04)}
        $color={t.sub}
      >
        <Icon name="more-horizontal" size={18} color="currentColor" />
      </MobileButton>
    </div>
  );
};

MobileActions.propTypes = {
  name: PropTypes.string.isRequired,
  onEdit: PropTypes.func.isRequired,
  onMore: PropTypes.func.isRequired,
};

const cardColors = ({ theme, t, selected, error }) => {
  if (error) {
    return {
      $border: ERROR_RED,
      $bg: 'rgba(237, 67, 55, 0.05)',
      $shadow: '0 0 0 3px rgba(237, 67, 55, 0.14)',
      $hoverBorder: ERROR_RED,
    };
  }
  if (selected) {
    return {
      $border: t.primary,
      $bg: orange(0.07),
      $shadow: `0 0 0 3px ${orange(0.16)}`,
      $hoverBorder: t.primary,
    };
  }
  return {
    $border: inkA(theme, 0.1),
    $bg: inkA(theme, 0.025),
    $shadow: 'none',
    $hoverBorder: inkA(theme, 0.18),
  };
};

export const fieldName = (field) =>
  (field.label || '').trim() || fieldTypeLabel(field);

const ErrorLine = ({ message }) =>
  message ? (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        marginTop: 10,
        fontSize: 12.5,
        fontWeight: 600,
        color: ERROR_RED,
      }}
    >
      <Icon name="triangle-alert" size={14} color="currentColor" />
      {message}
    </div>
  ) : null;

ErrorLine.propTypes = { message: PropTypes.string };
ErrorLine.defaultProps = { message: null };

const DragHandle = ({ mapped }) => {
  const theme = useTheme();
  const t = tokens(theme);
  return (
    <Handle
      className={DRAG_HANDLE_CLASS}
      data-help="drag-handle"
      role="presentation"
      onClick={(e) => e.stopPropagation()}
      title="Drag to reorder"
      $color={t.faint}
      $bg={inkA(theme, 0.045)}
      $line={t.border}
    >
      {mapped ? <SourceMark size={20} /> : null}
      <Icon name="grip-vertical" size={18} color="currentColor" />
    </Handle>
  );
};

DragHandle.propTypes = { mapped: PropTypes.bool.isRequired };

const reserveFor = (readOnly, mobile, locked) => {
  if (readOnly) return 0;
  if (locked) return 36;
  return mobile ? 100 : ACTION_ROOM;
};

// True once the card's content is measured under NARROW_CARD px. jsdom (no
// layout) measures 0 and keeps the wide layout.
export const useNarrowCard = (ref, enabled) => {
  const [narrow, setNarrow] = useState(false);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!enabled || !el) return undefined;
    const measure = () => {
      const w = el.clientWidth;
      setNarrow(w > 0 && w < NARROW_CARD);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref, enabled]);
  return narrow;
};

const FieldCard = ({
  field,
  variant,
  selected,
  error,
  readOnly,
  assetUrl,
  uploading,
  onSelect,
  onDuplicate,
  onDelete,
  onEdit,
  onMore,
  onPickAsset,
  locked,
  className,
  ...rest
}) => {
  const theme = useTheme();
  const t = tokens(theme);
  const bodyRef = useRef(null);
  const mobile = variant === 'mobile';
  const editable = !readOnly && !locked;
  const name = fieldName(field);
  const narrow = useNarrowCard(bodyRef, !mobile);
  const tags = mobile || narrow ? 'below' : 'side';
  const activate = () => {
    if (locked) return;
    if (mobile && editable) onEdit();
    else onSelect();
  };
  const actions = mobile ? (
    <MobileActions name={name} onEdit={onEdit} onMore={onMore} />
  ) : (
    <DesktopActions name={name} onDuplicate={onDuplicate} onDelete={onDelete} />
  );
  return (
    <Card
      id={`fb-field-${field.id}`}
      data-tour="field-card"
      className={className}
      {...rest}
      {...cardColors({ theme, t, selected, error })}
      $showActions={selected}
      $slideTags={editable && tags === 'side'}
      data-locked={locked ? '' : undefined}
    >
      {editable && !mobile ? <DragHandle mapped={!!field.map} /> : null}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          padding: mobile ? '13px 14px 14px' : '13px 16px 15px 14px',
        }}
      >
        <div ref={bodyRef}>
          <FieldPreview
            field={field}
            assetUrl={assetUrl}
            uploading={uploading}
            onPickAsset={editable ? onPickAsset : undefined}
            onActivate={activate}
            reserve={reserveFor(readOnly, mobile, locked)}
            labelMark={mobile ? <SourceMark size={18} /> : null}
            tags={tags}
          />
          <ErrorLine message={error} />
        </div>
      </div>
      <SelectButton
        data-fb-select=""
        aria-label={`${name}, ${fieldTypeLabel(field)} field${
          locked ? ` (${LOCKED_TIP})` : ''
        }${error ? `: ${error}` : ''}`}
        aria-pressed={mobile || locked ? undefined : selected}
        aria-disabled={locked || undefined}
        title={locked ? LOCKED_TIP : undefined}
        onClick={(e) => {
          e.stopPropagation();
          activate();
        }}
      />
      {editable ? actions : null}
      {locked && !readOnly ? <LockBadge name={name} /> : null}
    </Card>
  );
};

FieldCard.propTypes = {
  field: PropTypes.object.isRequired,
  variant: PropTypes.oneOf(['desktop', 'mobile']),
  selected: PropTypes.bool,
  error: PropTypes.string,
  readOnly: PropTypes.bool,
  assetUrl: PropTypes.func,
  uploading: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onMore: PropTypes.func,
  onPickAsset: PropTypes.func,
  locked: PropTypes.bool,
  className: PropTypes.string,
};

const noop = () => undefined;

FieldCard.defaultProps = {
  variant: 'desktop',
  selected: false,
  error: null,
  readOnly: false,
  assetUrl: () => null,
  uploading: false,
  onDuplicate: noop,
  onDelete: noop,
  onEdit: noop,
  onMore: noop,
  onPickAsset: undefined,
  locked: false,
  className: undefined,
};

export default FieldCard;
